import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, UserProfile } from '../types';
import { Save, Building2, MapPin, Phone, FileText, Globe, Loader2, CheckCircle, AlertTriangle, Upload } from 'lucide-react';
import { api } from '../services/api';

interface ProfileFormProps {
    user: User;
    onSave?: () => void;
    isReadOnly?: boolean;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({ user, onSave, isReadOnly = false }) => {
    const { t } = useTranslation('settings');
    const [profile, setProfile] = useState<UserProfile>({
        companyName: '',
        vatNumber: '',
        responsiblePerson: '',
        taxOffice: '',
        activity: '',
        address: { street: '', city: '', postalCode: '', country: 'GR' },
        contact: { mobile: '', phone: '' },
        notes: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [isVatVerified, setIsVatVerified] = useState(false);

    // Initialize from user.profile
    useEffect(() => {
        if (user.profile) {
            setProfile(user.profile);
        }
    }, [user]);

    const handleChange = (section: keyof UserProfile | null, field: string, value: string) => {
        if (isReadOnly) return;

        if (section && typeof profile[section] === 'object') {
            setProfile(prev => ({
                ...prev,
                [section]: {
                    ...prev[section] as any,
                    [field]: value
                }
            }));
        } else {
            setProfile(prev => ({
                ...prev,
                [field]: value
            }));
        }

        // Reset verification if VAT changes manually
        if (field === 'vatNumber') {
            setIsVatVerified(false);
        }
    };


    const validateForm = () => {
        const vatRegex = /^([A-Z]{2})?\d{9}$/;
        const phoneRegex = /^\d{10}$/;
        // const postalRegex = /^\d{5}$/; // Optional check

        if (profile.address.country === 'GR') {
            // If verified (e.g. from VIES as EL123...), valid. If manual, must be 9 digits.
            if (!isVatVerified && !vatRegex.test(profile.vatNumber)) {
                setMsg({ type: 'error', text: t('profile.messages.invalid_vat') });
                return false;
            }
            // Phone validation removed as per request
        }
        return true;
    };

    const handleValidateVat = async () => {
        if (!profile.vatNumber || profile.vatNumber.length < 9) {
            setMsg({ type: 'error', text: t('profile.messages.enter_vat_first') });
            return;
        }

        setIsLoading(true);
        setMsg(null);
        try {
            // Strip country code prefix (e.g. EL) before sending to API, as backend expects digits only
            const cleanVat = profile.vatNumber.replace(/^[A-Z]{2}/i, '');
            const result = await api.validateVat(cleanVat, profile.address.country);
            if (result.valid) {
                setProfile(prev => ({
                    ...prev,
                    companyName: result.name || prev.companyName,
                    address: {
                        ...prev.address,
                        street: result.address || prev.address.street
                    }
                }));
                setIsVatVerified(true);
                setMsg({ type: 'success', text: t('profile.messages.vat_verified') });
            } else {
                setMsg({ type: 'error', text: t('profile.messages.vat_not_found') });
            }
        } catch (e: any) {
            setMsg({ type: 'error', text: 'Validation failed: ' + e.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMsg(null);

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            await api.updateUserProfile(user.id, profile);
            setMsg({ type: 'success', text: t('profile.messages.profile_updated') });
            if (onSave) onSave();
        } catch (error: any) {
            setMsg({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <form onSubmit={handleSubmit} className="space-y-6">

            {msg && (
                <div className={`p-4 rounded-xl text-sm font-medium flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {msg.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                    {msg.text}
                </div>
            )}

            {/* 1. Identity Section */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 size={20} className="text-slate-500" />
                    {t('profile.identity_title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 flex items-center gap-6 mb-2">
                        {/* Logo Upload */}
                        <div className="relative group">
                            <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                                onClick={() => !isReadOnly && document.getElementById('logo-upload')?.click()}
                            >
                                {profile.logoUrl ? (
                                    <img src={profile.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                                ) : (
                                    <div className="text-slate-400 text-xs text-center px-2">
                                        <div className="mb-1 mx-auto"><Upload size={16} /></div>
                                        {t('profile.upload_logo')}
                                    </div>
                                )}
                                {!isReadOnly && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
                                        <Upload className="text-white" size={20} />
                                    </div>
                                )}
                            </div>
                            <input
                                id="logo-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setIsLoading(true);
                                        try {
                                            const file = e.target.files[0];
                                            const url = await api.uploadFile(`logos/${user.id}_${Date.now()}_${file.name}`, file);
                                            setProfile(prev => ({ ...prev, logoUrl: url }));
                                        } catch (err: any) {
                                            setMsg({ type: 'error', text: 'Failed to upload logo: ' + err.message });
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div className="flex-1">
                            <p className="font-bold text-slate-900">{t('profile.business_logo')}</p>
                            <p className="text-xs text-slate-500">{t('profile.logo_help')}</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.company_name')} *</label>
                        <input
                            type="text"
                            required
                            disabled={isReadOnly}
                            value={profile.companyName}
                            onChange={(e) => handleChange(null, 'companyName', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.vat_number')} *</label>
                        <p className="text-[10px] text-slate-400 mb-2">Add before the VAT number the country code (e.g. for Greece ELXXXXXXXX)</p>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                required
                                disabled={isReadOnly}
                                value={profile.vatNumber}
                                onChange={(e) => handleChange(null, 'vatNumber', e.target.value)}
                                className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                                placeholder="9 digits (e.g. 090123456)"
                            />
                            {!isReadOnly && (
                                <button type="button" onClick={handleValidateVat} disabled={isLoading} className="bg-blue-50 text-blue-600 px-3 py-2 rounded-lg font-bold text-xs hover:bg-blue-100 transition whitespace-nowrap">
                                    {t('profile.check_vat')}
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.responsible_person')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.responsiblePerson || ''}
                            onChange={(e) => handleChange(null, 'responsiblePerson', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                </div>
            </div>

            {/* 2. Tax Details */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-slate-500" />
                    {t('profile.tax_details_title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.tax_office')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.taxOffice || ''}
                            onChange={(e) => handleChange(null, 'taxOffice', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.activity')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.activity || ''}
                            onChange={(e) => handleChange(null, 'activity', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                </div>
            </div>

            {/* 3. Address */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <MapPin size={20} className="text-slate-500" />
                    {t('profile.address_title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.street')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.address.street}
                            onChange={(e) => handleChange('address', 'street', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.city')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.address.city}
                            onChange={(e) => handleChange('address', 'city', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.postal_code')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.address.postalCode}
                            onChange={(e) => handleChange('address', 'postalCode', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                </div>
            </div>

            {/* 4. Contact */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Phone size={20} className="text-slate-500" />
                    {t('profile.contact_title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.mobile')} *</label>
                        <input
                            type="text"
                            required
                            disabled={isReadOnly}
                            value={profile.contact.mobile}
                            onChange={(e) => handleChange('contact', 'mobile', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.landline')}</label>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            value={profile.contact.phone || ''}
                            onChange={(e) => handleChange('contact', 'phone', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.contact_email')}</label>
                        <input
                            type="email"
                            disabled={isReadOnly}
                            value={profile.contact.email || ''}
                            onChange={(e) => handleChange('contact', 'email', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                            placeholder="support@company.com"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.website')}</label>
                        <input
                            type="url"
                            disabled={isReadOnly}
                            value={profile.contact.website || ''}
                            onChange={(e) => handleChange('contact', 'website', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black disabled:bg-slate-50"
                            placeholder="https://company.com"
                        />
                    </div>
                </div>
            </div>

            <div className="flex justify-end">
                {!isReadOnly && (
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-black text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-800 transition flex items-center gap-2 shadow-lg"
                    >
                        {isLoading && <Loader2 className="animate-spin" size={18} />}
                        {t('profile.save_changes')}
                    </button>
                )}
            </div>
        </form>
    );
};
